import { useState } from "react";

/**
 * Renders a country flag using bundled local SVG assets for reliable desktop support.
 * Falls back to emoji text when no ISO code is provided or the asset is missing.
 */
interface FlagEmojiProps {
  iso?: string;
  flag?: string;
  size?: number;
  className?: string;
  /** Accessible label. Omit for decorative use (renders alt=""). */
  alt?: string;
}


/**
 * Derives an ISO2 code from a regional-indicator flag emoji (🇬🇧 → "gb").
 * Windows desktop has no flag glyphs, so it renders the raw letters ("GB").
 * Converting to an SVG asset makes flags render everywhere.
 */
export function isoFromFlagEmoji(flag?: string): string | null {
  if (!flag) return null;
  const points = Array.from(flag.trim())
    .map((ch) => ch.codePointAt(0) ?? 0)
    .filter((cp) => cp >= 0x1f1e6 && cp <= 0x1f1ff);
  if (points.length < 2) return null;
  return points
    .slice(0, 2)
    .map((cp) => String.fromCharCode(cp - 0x1f1e6 + 97))
    .join("");
}

const FlagEmoji = ({ iso, flag, size = 20, className = "", alt = "" }: FlagEmojiProps) => {
  const [failed, setFailed] = useState(false);
  const resolvedIso = iso || isoFromFlagEmoji(flag);

  if (resolvedIso && !failed) {
    const code = resolvedIso.toLowerCase();
    // Prefer locally bundled SVG (public/flags/*.svg) for reliability; fall back to flagcdn, then emoji.
    return (
      <img
        src={`/flags/${code}.svg`}
        alt={alt}

        width={size}
        height={Math.round(size * 0.75)}
        className={className}
        style={{ display: "inline-block", verticalAlign: "middle", objectFit: "contain", flexShrink: 0 }}
        loading="lazy"
        onError={(e) => {
          const img = e.currentTarget;
          if (!img.dataset.fallback) {
            img.dataset.fallback = "1";
            img.src = `https://flagcdn.com/${code}.svg`;
          } else {
            setFailed(true);
          }
        }}
      />
    );
  }

  return (
    <span
      className={className}
      style={{
        fontFamily: '"Twemoji Mozilla", "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif',
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: Math.round(size * 0.75),
        flexShrink: 0,
      }}
    >
      {flag || "🌍"}
    </span>
  );
};

export default FlagEmoji;
